/* eslint-disable @typescript-eslint/no-unsafe-argument */
import React, { FC, useState, useEffect, useRef, useMemo } from 'react';
import { styled } from 'linaria/lib/react';
import { theme, useDidMount } from '@mtsbank/ui-kit';
import { Payee } from '@root/api/ump-catalog-manager';
import { serviceClickGtm, tabClickGtm } from '@root/utils/gtm/cyber/events';
import { TileButton } from './buttons/TileButton';
import { TabButton } from './buttons/TabButton';
import { Tabs } from './types';
import { Spacer } from '../Uikit/Spacer';
type ServicesSwitchProps = {
  services?: Record<Tabs, Payee[]>;
};

// Добавляем маппинг URL => Tab
const URL_TO_TAB: Record<string, Tabs> = {
  games: Tabs.GAMES,
  services: Tabs.SERVICES,
  programs: Tabs.PROGRAMS
};

// Добавляем обратный маппинг Tab => URL
const TAB_TO_URL: Record<Tabs, string> = {
  [Tabs.GAMES]: 'games',
  [Tabs.SERVICES]: 'services',
  [Tabs.PROGRAMS]: 'programs',
  [Tabs.TOP]: 'top'
};
export const ServicesSwitch: FC<ServicesSwitchProps> = ({
  services
}) => {
  const [activeTab, setActiveTab] = useState<string>(null);
  const componentRef = useRef<HTMLDivElement>(null);
  useDidMount(() => {
    if (typeof window === 'undefined') return;
    const hash = window.location.hash.slice(1).toLowerCase();
    const tab = URL_TO_TAB[hash];
    setActiveTab(tab || Tabs.GAMES);
    if (tab) {
      const scrollToComponent = () => {
        const topOffset = (componentRef.current?.getBoundingClientRect()?.top ?? 0) + window.scrollY - 80;
        if (Number.isFinite(topOffset)) {
          window.scrollTo({
            top: topOffset,
            behavior: 'smooth'
          });
        }
      };
      requestAnimationFrame(scrollToComponent);
    }
  });
  const handleTabChange = (tab: string, id: string) => {
    setActiveTab(tab);
    if (typeof window !== 'undefined') {
      window.location.hash = TAB_TO_URL[tab as Tabs];
    }
    tabClickGtm(tab);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest'
      });
    }
  };
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const handleHashChange = () => {
        const hash = window.location.hash.slice(1).toLowerCase();
        const tab = URL_TO_TAB[hash];
        if (tab) {
          setActiveTab(tab);
        }
      };
      window.addEventListener('hashchange', handleHashChange);
      return () => window.removeEventListener('hashchange', handleHashChange);
    }
  }, []);
  const tabButtons = [{
    category: Tabs.GAMES,
    icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g clipPath="url(#clip0_4773_16835)">
            <path fillRule="evenodd" clipRule="evenodd" d="M12 4C10.4502 4 9.09788 4.06763 7.94509 4.16772C5.92076 4.34348 4.9086 4.43136 3.61923 5.72515C2.32986 7.01894 2.24897 7.98922 2.08718 9.92978C2.03307 10.5788 2 11.2729 2 12C2 14.0039 2.25114 15.7568 2.5018 17.0072C2.75194 18.2552 3.76664 19.1602 5.01241 19.421C6.24518 19.679 7.52023 19.269 8.39221 18.3602C9.59819 17.1032 11.196 15.5 11.5 15.5H12.5C12.804 15.5 14.4018 17.1032 15.6078 18.3601C16.4798 19.269 17.7548 19.679 18.9876 19.4211C20.2338 19.1603 21.2487 18.2548 21.4987 17.0065C21.7491 15.756 22 14.0035 22 12C22 11.2736 21.967 10.5802 21.913 9.9317C21.7514 7.9901 21.6706 7.0193 20.3811 5.72528C19.0917 4.43126 18.079 4.34338 16.0538 4.16761C14.9012 4.06758 13.5492 4 12 4ZM15.5 10.5C15.5 11.0523 15.0523 11.5 14.5 11.5C13.9477 11.5 13.5 11.0523 13.5 10.5C13.5 9.94772 13.9477 9.5 14.5 9.5C15.0523 9.5 15.5 9.94772 15.5 10.5ZM18 11.5C18.5523 11.5 19 11.0523 19 10.5C19 9.94772 18.5523 9.5 18 9.5C17.4477 9.5 17 9.94772 17 10.5C17 11.0523 17.4477 11.5 18 11.5ZM16.25 11.25C16.8023 11.25 17.25 11.6977 17.25 12.25C17.25 12.8023 16.8023 13.25 16.25 13.25C15.6977 13.25 15.25 12.8023 15.25 12.25C15.25 11.6977 15.6977 11.25 16.25 11.25ZM17.25 8.75C17.25 8.19772 16.8023 7.75 16.25 7.75C15.6977 7.75 15.25 8.19772 15.25 8.75C15.25 9.30228 15.6977 9.75 16.25 9.75C16.8023 9.75 17.25 9.30228 17.25 8.75ZM7.75 7.75C8.30228 7.75 8.75 8.19772 8.75 8.75V9.5H9.5C10.0523 9.5 10.5 9.94772 10.5 10.5C10.5 11.0523 10.0523 11.5 9.5 11.5H8.75V12.25C8.75 12.8023 8.30228 13.25 7.75 13.25C7.19772 13.25 6.75 12.8023 6.75 12.25V11.5H6C5.44772 11.5 5 11.0523 5 10.5C5 9.94772 5.44772 9.5 6 9.5H6.75V8.75C6.75 8.19772 7.19772 7.75 7.75 7.75Z" fill="#FAFAFA" />
          </g>
          <defs>
            <clipPath id="clip0_4773_16835">
              <rect width="24" height="24" fill="white" />
            </clipPath>
          </defs>
        </svg>
  }, {
    category: Tabs.SERVICES,
    icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g clipPath="url(#clip0_5777_8797)">
            <path fillRule="evenodd" clipRule="evenodd" d="M13.6884 9.24782C13.0702 8.53013 12.761 8.17128 12.761 7.50082C12.761 6.83035 13.0702 6.4715 13.6884 5.75381C13.8487 5.56777 14.0195 5.38134 14.1981 5.20272C14.3767 5.0241 14.5632 4.85329 14.7492 4.69302C15.4669 4.07476 15.8257 3.76562 16.4962 3.76562C17.1667 3.76562 17.5255 4.07476 18.2432 4.69302C18.4293 4.85329 18.6157 5.0241 18.7943 5.20272C18.9729 5.38134 19.1437 5.56777 19.304 5.75381C19.9223 6.4715 20.2314 6.83035 20.2314 7.50081C20.2314 8.17128 19.9223 8.53013 19.304 9.24782C19.1437 9.43386 18.9729 9.62029 18.7943 9.79891C18.6157 9.97753 18.4293 10.1483 18.2432 10.3086C17.5255 10.9269 17.1667 11.236 16.4962 11.236C15.8258 11.236 15.4669 10.9269 14.7492 10.3086C14.5632 10.1483 14.3767 9.97753 14.1981 9.79891C14.0195 9.62029 13.8487 9.43386 13.6884 9.24782ZM4.65386 4.65577C4.14317 5.16646 4.10531 5.67525 4.02958 6.69283C4.00994 6.95661 3.99805 7.22871 3.99805 7.50081C3.99805 7.77292 4.00994 8.04502 4.02958 8.3088C4.10531 9.32638 4.14317 9.83517 4.65386 10.3459C5.16455 10.8565 5.67334 10.8944 6.69091 10.9701C6.95469 10.9898 7.2268 11.0017 7.4989 11.0017C7.771 11.0017 8.04311 10.9898 8.30689 10.9701C9.32447 10.8944 9.83326 10.8565 10.3439 10.3459C10.8546 9.83517 10.8925 9.32638 10.9682 8.3088C10.9879 8.04502 10.9998 7.77292 10.9998 7.50081C10.9998 7.22871 10.9879 6.95661 10.9682 6.69283C10.8925 5.67525 10.8546 5.16646 10.3439 4.65577C9.83326 4.14509 9.32447 4.10722 8.30689 4.03149C8.04311 4.01186 7.771 3.99996 7.4989 3.99996C7.2268 3.99996 6.95469 4.01186 6.69091 4.03149C5.67334 4.10722 5.16455 4.14509 4.65386 4.65577ZM13.0315 15.6922C13.1072 14.6748 13.1451 14.1662 13.6557 13.6556C14.1662 13.1451 14.6749 13.1072 15.6922 13.0315C15.9559 13.0119 16.228 13 16.5 13C16.772 13 17.0441 13.0119 17.3078 13.0315C18.3251 13.1072 18.8338 13.1451 19.3443 13.6556C19.8549 14.1662 19.8928 14.6748 19.9685 15.6922C19.9881 15.9559 20 16.2279 20 16.5C20 16.772 19.9881 17.044 19.9685 17.3078C19.8928 18.3251 19.8549 18.8337 19.3443 19.3443C18.8338 19.8549 18.3251 19.8927 17.3078 19.9684C17.0441 19.9881 16.772 20 16.5 20C16.228 20 15.9559 19.9881 15.6922 19.9684C14.6749 19.8927 14.1662 19.8549 13.6557 19.3443C13.1451 18.8337 13.1072 18.3251 13.0315 17.3078C13.0119 17.044 13 16.772 13 16.5C13 16.2279 13.0119 15.9559 13.0315 15.6922ZM4.03152 15.6923C4.10724 14.6749 4.1451 14.1662 4.65568 13.6556C5.16626 13.1451 5.67494 13.1072 6.6923 13.0315C6.95603 13.0119 7.22808 13 7.50012 13C7.77217 13 8.04422 13.0119 8.30794 13.0315C9.32531 13.1072 9.83399 13.1451 10.3446 13.6556C10.8551 14.1662 10.893 14.6749 10.9687 15.6923C10.9884 15.956 11.0002 16.228 11.0002 16.5001C11.0002 16.7721 10.9884 17.0442 10.9687 17.3079C10.893 18.3253 10.8551 18.8339 10.3446 19.3445C9.83399 19.8551 9.32531 19.893 8.30794 19.9687C8.04422 19.9883 7.77217 20.0002 7.50012 20.0002C7.22808 20.0002 6.95603 19.9883 6.6923 19.9687C5.67494 19.893 5.16626 19.8551 4.65568 19.3445C4.1451 18.8339 4.10724 18.3253 4.03152 17.3079C4.01189 17.0442 4 16.7721 4 16.5001C4 16.228 4.01189 15.956 4.03152 15.6923Z" fill="#747796" />
          </g>
          <defs>
            <clipPath id="clip0_5777_8797">
              <rect width="24" height="24" fill="white" />
            </clipPath>
          </defs>
        </svg>
  }, {
    category: Tabs.PROGRAMS,
    icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g clipPath="url(#clip0_5777_1071)">
            <path fillRule="evenodd" clipRule="evenodd" d="M12.0043 3C10.4544 3 9.1019 3.06764 7.94903 3.16774C5.92469 3.3435 4.91251 3.43138 3.62315 4.72517C2.33378 6.01895 2.25288 6.98923 2.09109 8.92979C2.03697 9.57882 2.00391 10.2729 2.00391 11C2.00391 11.7272 2.03698 12.4213 2.0911 13.0704C2.2529 15.0109 2.3338 15.9811 3.62314 17.2749C4.91249 18.5687 5.92462 18.6565 7.94887 18.8323C9.10178 18.9324 10.4543 19.0001 12.0043 19.0001C13.5537 19.0001 14.9059 18.9325 16.0586 18.8324C18.0837 18.6566 19.0963 18.5687 20.3857 17.2747C21.6752 15.9807 21.756 15.01 21.9176 13.0685C21.9716 12.42 22.0046 11.7265 22.0046 11C22.0046 10.2736 21.9716 9.58019 21.9176 8.9317C21.756 6.99011 21.6752 6.01931 20.3857 4.7253C19.0963 3.43128 18.0836 3.3434 16.0584 3.16763C14.9057 3.06759 13.5536 3 12.0043 3ZM13.0002 8C13.0002 7.44772 12.5525 7 12.0002 7C11.448 7 11.0002 7.44772 11.0002 8V14C11.0002 14.5523 11.448 15 12.0002 15C12.5525 15 13.0002 14.5523 13.0002 14V8ZM8.00024 10C8.55253 10 9.00024 10.4477 9.00024 11V14C9.00024 14.5523 8.55253 15 8.00024 15C7.44796 15 7.00024 14.5523 7.00024 14V11C7.00024 10.4477 7.44796 10 8.00024 10ZM17.0002 10C17.0002 9.44772 16.5525 9 16.0002 9C15.448 9 15.0002 9.44772 15.0002 10V14C15.0002 14.5523 15.448 15 16.0002 15C16.5525 15 17.0002 14.5523 17.0002 14V10Z" fill="#747796" />
            <path d="M7.00413 21C7.00413 20.4477 7.45185 20 8.00413 20H16.0041C16.5564 20 17.0041 20.4477 17.0041 21C17.0041 21.5523 16.5564 22 16.0041 22H8.00413C7.45185 22 7.00413 21.5523 7.00413 21Z" fill="#747796" />
          </g>
          <defs>
            <clipPath id="clip0_5777_1071">
              <rect width="24" height="24" fill="white" />
            </clipPath>
          </defs>
        </svg>
  }];
  const sortedServices = useMemo(() => {
    if (!services?.[activeTab]) return [];
    if (activeTab === Tabs.GAMES) {
      return [...services[activeTab]].sort((a, b) => {
        const urlA = a?.features?.tsp_url || '';
        const urlB = b?.features?.tsp_url || '';
        const priorityGames = ['marvel-rivals', 'zenless-zone-zero', 'honkai-star-rail', 'arena-breakout', 'dragonheir-silent-gods', 'super-sus', 'state-of-survival-zombie-war', 'onmyoji-arena', 'goddess-of-victory-nikke', 'harry-potter-magic-awakened', 'lifeafter', 'watcher-of-realms'];
        const isPriorityA = priorityGames.includes(urlA);
        const isPriorityB = priorityGames.includes(urlB);
        if (isPriorityA && isPriorityB) {
          return priorityGames.indexOf(urlA) - priorityGames.indexOf(urlB);
        }
        if (isPriorityA) return -1;
        if (isPriorityB) return 1;
        return (b?.providerName || '').localeCompare(a?.providerName || '');
      });
    }
    return services[activeTab];
  }, [services, activeTab]);
  const serviceElements = useMemo(() => sortedServices.map(serviceInfo => <TileButton key={serviceInfo?.serviceId} onClick={() => serviceClickGtm(serviceInfo.providerName)} title={serviceInfo?.providerName} icon={serviceInfo?.features?.tsp_picture_web} url={serviceInfo?.features?.tsp_url as string} />), [sortedServices]);
  return <div ref={componentRef}>
      <TabButtonGroup>
        {tabButtons.map(({
        category,
        icon
      }, index) => {
        let id = '';
        if (index === 0) {
          id = 'left';
        } else if (index === tabButtons.length - 1) {
          id = 'right';
        }
        return <TabButton main key={category} title={category} onClick={() => handleTabChange(category, id)} activeTab={activeTab} icon={icon} id={id} />;
      })}
      </TabButtonGroup>
      <Spacer space={32} />
      <ButtonGroup>{serviceElements}</ButtonGroup>
    </div>;
};
const TabButtonGroup = /*#__PURE__*/styled("div")({
  name: "TabButtonGroup",
  class: "t1ekplk3"
});
const ButtonGroup = /*#__PURE__*/styled("div")({
  name: "ButtonGroup",
  class: "b50fhp1"
});

require("./ServicesSwitch.linaria.module.css!=!../../../node_modules/linaria/lib/outputCssLoader.js?cacheProvider=!./ServicesSwitch.tsx");